import { i18nNsFactory } from '@atlas-frontend/i18n';
import { RadioChangeEvent } from 'antd/es/radio/interface';
import { useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormField, FormRadioGroup } from '../../common/form';
import {
  useSystemClassifierItem,
  useSystemClassifierSelectOptions,
} from '../../configuration';
import translations from '../translations/LoadingSection.translations.json';
import { LoadingPeriodFields } from './LoadingPeriodFields';
import { ExecutionMethod } from './types';
import { DataObjectGroupType } from '../types';

const { addTranslations, useTranslation } = i18nNsFactory(
  'execution-loading-section'
);
addTranslations(translations);

export interface LoadingSectionProps {
  executionMethod?: string;
  isInput: boolean;
  groupType: DataObjectGroupType;
}

export function LoadingSection({
  executionMethod,
  isInput,
  groupType,
}: LoadingSectionProps) {
  const { t } = useTranslation();
  const options = useExecutionMethodOptions(isInput);
  const fixedLoadingType = useSystemClassifierItem(
    'PERIOD_LOADING_TYPE',
    'FIXED'
  )?.id;
  const variableLoadingType = useSystemClassifierItem(
    'PERIOD_LOADING_TYPE',
    'VARIABLE'
  )?.id;
  const latestSourceDataVersion = useSystemClassifierItem(
    'SOURCE_DATA_VERSION',
    'LATEST'
  )?.id;
  const { setValue } = useFormContext();

  const changeMethod = useCallback(
    (e: RadioChangeEvent) => {
      const method = e.target.value as number;
      const selected = options.find((m) => m.value === method)?.code;
      const opts = { shouldDirty: true };
      if (selected === ExecutionMethod.MANUAL) {
        // Reset loading period fields if manual method selected
        setValue('execution.periodLoadingTypeId', null, opts);
        setValue('execution.periodCount', null, opts);
        setValue('execution.periodSkipCount', null, opts);
        setValue('execution.executionTime', null, opts);
        setValue('execution.executionCalendar', [], opts);
        setValue('execution.executionPeriods', [], opts);
      } else {
        if (executionMethod === ExecutionMethod.MANUAL) {
          // If changed from manual, set default loading period fields
          setValue('execution.periodCount', 1, opts);
          setValue('execution.periodLoadingTypeId', fixedLoadingType, opts);
        }
        // If changed to calendar, set default execution time
        if (selected === ExecutionMethod.CALENDAR) {
          setValue('execution.executionTime', '00:00', opts);
          setValue('execution.executionPeriods', [], opts);
          setValue('execution.periodLoadingTypeId', variableLoadingType, opts);
          setValue(
            'execution.sourceDataVersionId',
            latestSourceDataVersion,
            opts
          );
        } else {
          setValue('execution.executionTime', null, opts);
          setValue('execution.executionCalendar', [], opts);
          setValue('execution.periodLoadingTypeId', fixedLoadingType, opts);
          setValue(
            'execution.logLoadingSuccess',
            groupType === DataObjectGroupType.I,
            opts
          );
          setValue(
            'execution.sourceDataVersionId',
            latestSourceDataVersion,
            opts
          );
        }
      }
    },
    [
      executionMethod,
      fixedLoadingType,
      groupType,
      latestSourceDataVersion,
      options,
      setValue,
      variableLoadingType,
    ]
  );

  return (
    <>
      <FormField
        name="execution.executionMethodId"
        label={isInput ? t('executionMethodId.in') : t('executionMethodId.out')}
        required
      >
        <FormRadioGroup
          name="execution.executionMethodId"
          onChange={changeMethod}
          options={options}
        />
      </FormField>
      {executionMethod !== 'MANUAL' && (
        <LoadingPeriodFields
          groupType={groupType}
          executionMethod={executionMethod}
          isInput={isInput}
        />
      )}
    </>
  );
}

function useExecutionMethodOptions(isInput: boolean) {
  const methodClassifiers = useSystemClassifierSelectOptions([
    'EXECUTION_METHOD',
  ]).EXECUTION_METHOD;

  return useMemo(() => {
    if (isInput) {
      return methodClassifiers.filter(
        (option) => option.code !== ExecutionMethod.SOURCE_DRIVEN
      );
    }

    return methodClassifiers;
  }, [isInput, methodClassifiers]);
}
